import React, { useMemo } from 'react'
import ListenButton from '@/components/atoms/ListenButton/ListenButton'
// import * as styles from '@/components/organisms/Learning/LearningSytle.css'
// import * as spellStyles from '@/components/organisms/Learning/SpellCheckStyle.css'
import GradingState from '@/components/organisms/Grading/GradingState'

interface GradingAlphabetChoiceProps {
  type?: 'scoreCard' | 'gradingCard'
  activity?: any
  responseJson?: string
  showTitle?: boolean
}

const GradingAlphabetChoice = ({ type = 'scoreCard', activity, responseJson, showTitle = true }: GradingAlphabetChoiceProps) => {
  // 실제 데이터 파싱
  const { word, imageUrl, audioUrl, studentAnswer, correctAnswer, blankIndices } = useMemo(() => {
    if (!activity || !activity.items || activity.items.length === 0) {
      // 더미 데이터
      return {
        word: 'apple',
        imageUrl: null,
        audioUrl: null,
        studentAnswer: {},
        correctAnswer: 'apple',
        blankIndices: [0, 2, 4]
      };
    }

    // activity.items에서 데이터 가져오기
    const activityData = activity?.items?.[0] || {};
    let content: any = {};
    
    try {
      const contentJson = activityData.contentJson;
      if (contentJson) {
        content = typeof contentJson === 'string' ? JSON.parse(contentJson) : contentJson;
      }
    } catch (e) {
      console.error('contentJson 파싱 에러:', e);
    }

    const wordValue = content.word || content.text || 'apple';
    const imageUrlValue = content.wordImageUrl || content.imageUrl || content.image || null;
    const audioUrlValue = content.audioUrl || content.audio || null;

    // responseJson 파싱하여 학생 답변 가져오기
    let answer: any = {};
    let blanks: number[] = [];
    if (responseJson) {
      try {
        const parsed = JSON.parse(responseJson);
        // 맵/단일 구조 모두 지원: 우선 순위로 맵의 첫 값 → 단일 객체
        const isMapLike = parsed && typeof parsed === 'object' && Object.keys(parsed).some((k: string) => /^\d+$/.test(k));
        const resp = isMapLike ? (parsed['0'] ?? Object.values(parsed)[0] ?? {}) : parsed;
        // 응답 구조: { answer: { [index]: 'a-0' }, blankIndices: number[] }
        answer = resp.answer || {};
        blanks = Array.isArray(resp.blankIndices) ? resp.blankIndices : [];
      } catch (e) {
        console.error('responseJson 파싱 실패:', e);
      }
    }

    return {
      word: wordValue,
      imageUrl: imageUrlValue,
      audioUrl: audioUrlValue,
      studentAnswer: answer,
      correctAnswer: wordValue,
      blankIndices: blanks
    };
  }, [activity, responseJson]);

  const correctLetters = correctAnswer.split('');

  // 전체 정답 여부 계산
  const isAllCorrect = useMemo(() => {
    const keys = Object.keys(studentAnswer)
      .map(k => Number(k))
      .filter(n => !Number.isNaN(n) && n >= 0 && n < correctLetters.length);
    const indicesToCheckSource: number[] = Array.isArray(blankIndices) && blankIndices.length > 0 ? blankIndices : keys;
    // 유효한 인덱스만 검사 (범위를 벗어난 인덱스는 무시)
    const indicesToCheck = indicesToCheckSource.filter(idx => idx >= 0 && idx < correctLetters.length);
    if (indicesToCheck.length === 0) return false;
    // 모든 기대 인덱스가 채워졌는지
    const allFilled = indicesToCheck.every(idx => !!studentAnswer[idx]);
    if (!allFilled) return false;
    // 전부 일치하는지
    return indicesToCheck.every(idx => {
      const studentLetter = studentAnswer[idx];
      const correctLetter = correctLetters[idx] || '';
      const value = studentLetter ? String(studentLetter).split('-')[0] : '';
      return value && correctLetter && value.toLowerCase() === correctLetter.toLowerCase();
    });
  }, [blankIndices, studentAnswer, correctLetters]);

  const overallStatus = useMemo(() => {
    const hasAny = Object.keys(studentAnswer).length > 0;
    if (!hasAny) return 'grading' as const;
    return isAllCorrect ? 'correct' as const : 'incorrect' as const;
  }, [studentAnswer, isAllCorrect]);

  return (
    <div>
      <div className='flex flex-col gap-2'>
        <div className='flex justify-between items-center'>
          {showTitle ? (
            <p className='text-lg font-bold'>{activity?.title || '알파벳 고르기'}</p>
          ) : (
            <span />
          )}
          <GradingState status={overallStatus} />
        </div>
        
        {/* 이미지와 단어 */}
        <div className='relative p-5 bg-gray-50 rounded-2xl'>
          <div className='flex flex-col items-center gap-4'>
            {imageUrl ? (
              <img 
                src={imageUrl} 
                alt={word} 
                className="max-w-[150px] rounded-xl"
                onError={(e) => {
                  e.currentTarget.style.display = 'none';
                  e.currentTarget.nextElementSibling?.classList.remove('hidden');
                }}
              />
            ) : (
              <div className="flex items-center justify-center w-[150px] h-[150px] bg-gray-100 rounded-xl text-gray-400 font-bold text-sm">
                이미지가 없습니다
              </div>
            )}
            <div className="hidden flex items-center justify-center w-[150px] h-[150px] bg-gray-100 rounded-xl text-gray-400 font-bold text-sm">
              이미지가 없습니다
            </div>
            {audioUrl && (
              <ListenButton size={40} className="!p-1" audioSrc={audioUrl} />
            )}
          </div>
        </div>

        {/* 단어 빈칸 채우기 영역 */}
        <div className='p-5 bg-white border border-gray-100 rounded-2xl'>
          <p className='text-sm font-bold text-gray-500 mb-3'>학생 답변</p>
          <div className='flex flex-wrap gap-2 justify-center'>
            {correctLetters.map((correctLetter: string, idx: number) => {
              const treatAllAsBlank = !Array.isArray(blankIndices) || blankIndices.length === 0;
              const isBlank = treatAllAsBlank ? true : blankIndices.includes(idx);
              const studentLetter = studentAnswer[idx];
              const studentLetterValue = studentLetter ? studentLetter.split('-')[0] : '';
              const isCorrect = isBlank && studentLetterValue === correctLetter;
              const isWrong = isBlank && studentLetterValue && studentLetterValue !== correctLetter;
              
              let className = 'px-4 py-2 min-w-[48px] text-center rounded-lg border-2 text-xl font-bold ';
              
              if (!isBlank) {
                // 보이는 글자 (회색)
                className += 'bg-gray-100 text-gray-600 border-gray-200';
              } else if (isCorrect) {
                // 정답
                className += 'bg-green-50 text-green-700 border-green-500';
              } else if (isWrong) {
                // 오답
                className += 'bg-red-50 text-red-700 border-red-500';
              } else {
                // 아직 답변하지 않음
                className += 'bg-orange-50 text-orange-500 border-orange-200';
              }

              return (
                <div key={idx} className='relative'>
                  <div className={className}>
                    {isBlank ? (studentLetterValue || '_') : correctLetter}
                  </div>
                </div>
              );
            })}
          </div>

          {/* 정답 표시 (오답인 경우에만) */}
          {!isAllCorrect && Object.keys(studentAnswer).length > 0 && (
              <div className='mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex flex-col gap-2 md:flex-row md:items-center md:justify-between'>
              <p className='text-xs font-bold text-green-700 mb-1 md:mb-0'>정답</p>
              <div className='flex-1'>
              <div className='flex flex-wrap gap-2 justify-center'>
                {correctLetters.map((letter: string, idx: number) => {
                  const isBlank = blankIndices.includes(idx);
                  return (
                    <div 
                      key={idx} 
                      className={`px-4 py-2 min-w-[48px] text-center rounded-lg text-xl font-bold ${
                        isBlank 
                          ? 'bg-green-100 text-green-700 border-2 border-green-300' 
                          : 'text-gray-500'
                      }`}
                    >
                      {letter}
                    </div>
                  );
                })}
              </div>
              </div>
            </div>
          )}

          {/* 학생이 아직 답변하지 않은 경우 */}
          {Object.keys(studentAnswer).length === 0 && (
            <div className='mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg'>
              <p className='text-sm text-orange-600'>아직 답변하지 않았습니다.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default GradingAlphabetChoice

